<template>
	<div v-if="isUserAllowedInLevel2 || (isUserAllowedInLevel3 && isAuthenticatedUserInGroupAdmin)">
		<div>
			<BaseHeader :title="getTitle">
				<div class="document-header-actions">
					<!-- <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" :class="getSourceClass">
						{{ getSourceLabel }}
					</span> -->

					<div v-if="isCompanyDefinition" class="document-header-toggle">
						<span class="font-bold text-lg">
							Vorlage aktiv
						</span>

						<Toggle
							style="vertical-align: bottom"
							:active="definition.is_active"
							:disabled="isUpdatingStatus"
							@click.native.prevent="toggleOverrideStatus"
						/>
					</div>
				</div>
			</BaseHeader>
			<div class="main">
				<!-- Status Hinweis -->
				<div v-if="definition" class="document-definition-notice mb-4">
					<div class="flex items-center justify-between">
						<div class="min-w-0 text-left">
							<template v-if="isCompanyDefinition">
								<span class="font-medium">
									Eigene Dokumentvorlage
								</span>

								<span class="mx-2 ">·</span>

								<span>
									<template v-if="definition.is_active">
										Diese Vorlage wird aktuell für dein Unternehmen verwendet.
									</template>

									<template v-else>
										Deaktiviert – aktuell wird die Systemvorlage verwendet.
									</template>
								</span>
							</template>

							<template v-else-if="isSystemReadOnly">
								<span class="font-medium">
									Systemvorlage
								</span>

								<span class="mx-2 ">·</span>

								<span>
									Eigene Vorlage vorhanden – diese Vorlage ist nur lesbar.
								</span>
							</template>

							<template v-else>
								<span class="font-medium">
									Systemvorlage
								</span>

								<span class="mx-2 ">·</span>

								<span>
									Du bearbeitest die Systemvorlage.
								</span>
							</template>
						</div>

						<button
							v-if="isSystemReadOnly"
							type="button"
							class="ml-6 flex-shrink-0 text-connectProBlue font-medium hover:underline whitespace-nowrap"
							@click="openCompanyDefinition"
						>
							Zur eigenen Vorlage
						</button>
					</div>
				</div>

				<div v-if="definition" class="w-full">
					<!-- Arbeitsbereich -->
					<div class="document-workspace">
						<!-- Editor -->
						<div class="document-editor-panel">
							<div class="font-bold text-left mb-3">
								Dokumentinhalt
							</div>
							<DocumentMarkdownEditor
								ref="markdownEditor"
								v-model="contentMarkdown"
								:elements="elements"
								:readonly="isSystemReadOnly || isSaving"
								@change="onContentChanged"
							/>
							<div v-if="invalidPlaceholders.length" class="document-validation-notice mt-3">
								<div class="document-validation-notice-title">
									<font-awesome-icon :icon="['fas', 'exclamation-circle']" class="mr-2" />
									Unbekannte Dokumentelemente
								</div>

								<p class="mb-2">
									Einige Platzhalter sind nicht bekannt und können bei der PDF-Erstellung möglicherweise nicht ersetzt werden. Bitte überprüfe die
									folgenden Elemente:
								</p>

								<div class="document-validation-keys">
									<div v-for="placeholder in invalidPlaceholders" :key="placeholder" class="document-validation-key">
										{{ placeholder }}
									</div>
								</div>
							</div>
						</div>

						<!-- Preview -->
						<div class="document-preview-panel">
							<div class="font-bold text-left mb-3">
								Vorschau
							</div>

							<div v-if="previewError" class="mb-4 px-4 py-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
								Die Vorschau konnte nicht aktualisiert werden. Die angezeigte PDF kann veraltet sein.
							</div>

							<div v-if="pdfURL && uuid" class="document-pdf-preview">
								<PDFThumbnailPreview
									:key="uuid"
									:pdf-u-r-l="pdfURL"
									class="w-full cursor-pointer"
									:iframe-height="'740px'"
									:type="PDFTypes.LawyerMandate"
									:uuid="uuid"
									:disable-click="true"
									:scrollable="true"
									@error="previewError = true"
								/>

								<!-- Lade-Overlay -->
								<div v-if="isPDFLoading" class="document-preview-overlay">
									<div class="document-preview-overlay-content">
										<LoadingSpinner class="preview-loading-spinner" />
										<span>Vorschau wird aktualisiert ...</span>
									</div>
								</div>
							</div>

							<div v-else class="document-preview-empty">
								<LoadingSpinner v-if="isPDFLoading" />

								<span v-else>
									Keine Vorschau verfügbar.
								</span>
							</div>
						</div>
					</div>

					<!-- Elemente -->
					<div
						class="document-elements-panel"
						:class="{
							'document-elements-panel-readonly': isSystemReadOnly,
						}"
					>
						<button type="button" class="document-elements-toggle" @click="isElementsCollapsed = !isElementsCollapsed">
							<span class="font-bold text-left">
								Dokumentelemente
							</span>

							<font-awesome-icon :icon="['fas', isElementsCollapsed ? 'chevron-down' : 'chevron-up']" class="text-gray-500" />
						</button>

						<div v-show="!isElementsCollapsed" class="document-elements-content">
							<div v-if="isSystemReadOnly" class="mb-3 text-left text-xs text-gray-700">
								Die Dokumentelemente können in der Systemansicht angesehen, aber nicht eingefügt werden.
							</div>

							<DocumentElementPicker :elements="elements" :disabled="isSystemReadOnly" @select="insertElement" />
						</div>
					</div>
				</div>

				<div v-else class="flex flex-grow justify-center w-full opacity-75">
					<LoadingSpinner />
				</div>
			</div>

			<Footer class="mx-8" :is-back-button="true" @backButtonClick="$router.go(-1)">
				<template #continueButton>
					<SaveButton v-if="!isSystemReadOnly" class="w-24 btn-blue" :disabled="isSaving || !hasUnsavedChanges" @click="save">
						<span v-if="isSaving">
							Speichert...
						</span>

						<span v-else>
							{{ $t('general.save') }}
						</span>
					</SaveButton>
					<div v-else></div>
				</template>
			</Footer>
		</div>
	</div>

	<Forbidden v-else />
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import Forbidden from '@/views/default/Forbidden.vue';

import Authentication from '@/mixins/Authentication';
import AppContainerMixin from '@/mixins/AppContainerMixin';

import Footer from '@/components/damagecases/Footer.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import Toggle from '@/components/forms/Toggle.vue';

import DocumentDefinitionRepository from '@/api/repositories/DocumentDefinitionRepository';

import DocumentMarkdownEditor from '@/components/documentdefinitions/DocumentMarkdownEditor.vue';
import DocumentElementPicker from '@/components/documentdefinitions/DocumentElementPicker.vue';

import PDFThumbnailPreview from '@/components/damagecases/forms/PDFThumbnailPreview.vue';

import { PDFTypes } from '@/api/generated';

export default {
	name: 'DocumentDefinitionsEdit',

	components: {
		Toggle,
		PDFThumbnailPreview,
		SaveButton,
		Footer,
		Forbidden,
		LoadingSpinner,
		BaseHeader,
		DocumentMarkdownEditor,
		DocumentElementPicker,
	},

	mixins: [Authentication, AppContainerMixin],

	data() {
		return {
			definition: null,
			elements: [],
			contentMarkdown: '',
			savedRequestData: null,
			isLoading: false,
			isSaving: false,
			isUpdatingStatus: false,
			isPDFLoading: false,
			previewError: false,
			previewTimeoutID: null,
			uuid: '',
			pdfURL: '',
			isElementsCollapsed: true,
			areElementsLoaded: false,
			isSyncingRouteSource: false,
		};
	},

	computed: {
		PDFTypes() {
			return PDFTypes;
		},

		definitionKey() {
			return this.$route.params.key;
		},

		definitionSource() {
			return this.$route.query.source || null;
		},

		isCompanyDefinition() {
			return !!(this.definition && this.definition.source === 'company');
		},

		isSystemDefinition() {
			return !!(this.definition && this.definition.source === 'system');
		},

		isSystemReadOnly() {
			return !!(this.isSystemDefinition && this.definition.has_company_override);
		},

		definitionNoticeClass() {
			if (this.isSystemReadOnly) {
				return 'bg-gray-50 border-gray-200 text-gray-700';
			}
			if (this.isCompanyDefinition && this.definition.is_active) {
				return 'bg-blue-50 border-blue-200 text-gray-700';
			}
			if (this.isCompanyDefinition) {
				return 'bg-gray-50 border-gray-200 text-gray-700';
			}
			return 'bg-gray-50 border-gray-200 text-gray-700';
		},

		getTitle() {
			if (!this.definition) {
				return '';
			}

			if (this.isSystemReadOnly) {
				return `${this.definition.label} ansehen`;
			}

			return `${this.definition.label} ${this.$t('edit_low')}`;
		},

		getSourceLabel() {
			if (!this.definition) {
				return '';
			}

			return this.isCompanyDefinition ? 'Eigene Vorlage' : 'Systemvorlage';
		},

		getSourceClass() {
			if (!this.definition) {
				return '';
			}

			return this.isCompanyDefinition ? 'bg-connectProBlue text-white' : 'bg-gray-200 text-gray-700';
		},

		hasUnsavedChanges() {
			if (!this.definition || !this.savedRequestData || this.isSystemReadOnly) {
				return false;
			}

			return JSON.stringify(this.getRequestData()) !== JSON.stringify(this.savedRequestData);
		},
		availableElementKeys() {
			const keys = new Set();

			this.elements.forEach(group => {
				(group.sections || []).forEach(section => {
					(section.entries || []).forEach(entry => {
						if (entry && typeof entry.key === 'string') {
							keys.add(entry.key);
						}
					});
				});
			});

			return keys;
		},

		invalidPlaceholders() {
			if (!this.areElementsLoaded) {
				return [];
			}

			const markdown = this.contentMarkdown || '';
			const matches = markdown.match(/\{\{[^{}\n]*\}\}/g) || [];
			const invalid = new Set();

			matches.forEach(placeholder => {
				const key = placeholder
					.replace(/^\{\{/, '')
					.replace(/\}\}$/, '')
					.trim();

				if (!this.availableElementKeys.has(key)) {
					invalid.add(placeholder);
				}
			});

			return Array.from(invalid);
		},
	},

	mounted() {
		this.load();
	},

	beforeDestroy() {
		clearTimeout(this.previewTimeoutID);

		this.$store.commit('setConfirmLeaving', false);

		if (this.pdfURL) {
			URL.revokeObjectURL(this.pdfURL);
		}
	},

	watch: {
		definitionSource(newSource, oldSource) {
			if (newSource === oldSource || this.isSyncingRouteSource) {
				return;
			}
			this.reloadDefinition();
		},
	},

	methods: {
		load() {
			this.isLoading = true;

			Promise.all([this.fetchDefinition(), this.fetchElements()])
				.then(() => {
					if (this.definition) {
						this.getPreview();
					}
				})
				.finally(() => {
					this.isLoading = false;
				});
		},

		fetchDefinition() {
			return new DocumentDefinitionRepository()
				.getByKey(this.definitionKey, this.definitionSource)
				.then(response => {
					this.applyDefinition(response.data);
					this.setSavedRequestData();
				})
				.catch(error => {
					this.$swal(
						this.$t('general.error').toString(),
						this.$t('values_cannot_be_fetched').toString(),
						'error',
					);
				});
		},

		reloadDefinition() {
			clearTimeout(this.previewTimeoutID);

			this.$store.commit('setConfirmLeaving', false);

			this.definition = null;
			this.savedRequestData = null;
			this.contentMarkdown = '';
			this.pdfURL = null;
			this.uuid = null;
			this.previewError = false;

			return this.fetchDefinition().then(() => {
				if (this.definition) {
					return this.getPreview();
				}
			});
		},

		openCompanyDefinition() {
			if (this.definitionSource === 'company') {
				return;
			}

			this.$router.push({
				name: 'DocumentDefinitionsEdit',
				params: {
					...this.$route.params,
					key: this.definitionKey,
				},
				query: {
					...this.$route.query,
					source: 'company',
				},
			});
		},

		applyDefinition(definition) {
			this.definition = definition;

			this.contentMarkdown = definition.content_markdown || '';
		},

		fetchElements() {
			this.areElementsLoaded = false;

			return new DocumentDefinitionRepository()
				.getElements()
				.then(response => {
					this.elements = response.data;
					this.areElementsLoaded = true;
				})
				.catch(error => {
					this.elements = [];
					this.areElementsLoaded = false;
				});
		},

		onContentChanged() {
			if (this.isSystemReadOnly) {
				return;
			}
			this.updateLeaveWarning();
			clearTimeout(this.previewTimeoutID);
			this.previewTimeoutID = setTimeout(() => {
				this.getPreview();
			}, 1500);
		},

		updateLeaveWarning() {
			if (this.hasUnsavedChanges) {
				this.$store.commit('setConfirmLeaving', this.$t('leave_page'));

				return;
			}

			this.$store.commit('setConfirmLeaving', false);
		},

		insertElement(entry) {
			if (this.isSystemReadOnly || !entry || !entry.key || !this.$refs.markdownEditor) {
				return;
			}

			this.$refs.markdownEditor.insertText(`{{ ${entry.key} }}`);
		},

		async save() {
			if (!this.definition || this.isSaving || !this.hasUnsavedChanges) {
				return;
			}

			if (this.invalidPlaceholders.length) {
				const result = await this.$swal({
					title: 'Ungültige Dokumentelemente',
					text:
						'Die Dokumentvorlage enthält unbekannte Platzhalter. Diese können in der PDF möglicherweise nicht korrekt ersetzt werden. Trotzdem speichern?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Trotzdem speichern',
					cancelButtonText: 'Abbrechen',
				});

				if (!result.isConfirmed) {
					return;
				}
			}

			this.performSave();
		},

		performSave() {
			if (!this.definition || this.isSaving) {
				return;
			}
			const requestData = this.getRequestData();
			this.isSaving = true;

			new DocumentDefinitionRepository()
				.update(this.definitionKey, requestData, this.definitionSource)
				.then(async response => {
					this.applyDefinition(response.data);
					await this.syncRouteSource();
					await this.fetchDefinition();

					if (this.definition) {
						this.getPreview();
					}

					this.$store.commit('setConfirmLeaving', false);
					this.$swal(this.$t('general.success').toString(), this.getSaveSuccessMessage(), 'success');
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
				})
				.finally(() => {
					this.isSaving = false;
				});
		},

		syncRouteSource() {
			if (!this.definition?.source || this.$route.query.source === this.definition.source) {
				return Promise.resolve();
			}

			this.isSyncingRouteSource = true;

			return new Promise(resolve => {
				this.$router.replace(
					{
						name: this.$route.name,
						params: {
							...this.$route.params,
						},
						query: {
							...this.$route.query,
							source: this.definition.source,
						},
					},
					() => {
						this.$nextTick(() => {
							this.isSyncingRouteSource = false;
							resolve();
						});
					},
					() => {
						this.isSyncingRouteSource = false;
						resolve();
					},
				);
			});
		},

		getRequestData() {
			if (!this.definition) {
				return {};
			}

			return {
				content_markdown: this.contentMarkdown,
				layout_key: this.definition.layout_key,
				layout_config: this.definition.layout_config || {},
			};
		},

		setSavedRequestData() {
			this.savedRequestData = JSON.parse(JSON.stringify(this.getRequestData()));
		},

		getSaveSuccessMessage() {
			if (this.definition && this.definition.source === 'company') {
				return 'Die eigene Dokumentvorlage wurde erfolgreich gespeichert.';
			}

			return 'Die Systemvorlage wurde erfolgreich gespeichert.';
		},

		toggleOverrideStatus() {
			if (!this.definition || this.definition.source !== 'company' || this.isUpdatingStatus) {
				return;
			}

			const nextIsActive = !this.definition.is_active;
			this.isUpdatingStatus = true;

			new DocumentDefinitionRepository()
				.updateOverrideStatus(this.definitionKey, nextIsActive)
				.then(response => {
					if (response && response.data && typeof response.data.is_active === 'boolean') {
						this.definition.is_active = response.data.is_active;
					} else {
						this.definition.is_active = nextIsActive;
					}
					this.$swal(
						this.$t('general.success').toString(),

						this.definition.is_active
							? 'Die eigene Vorlage wird jetzt verwendet.'
							: 'Die eigene Vorlage wurde deaktiviert. Die Systemvorlage wird jetzt verwendet.',

						'success',
					);
				})
				.catch(error => {
					this.$swal(
						this.$t('general.error').toString(),
						this.$t('general.changes_not_saved').toString(),
						'error',
					);
				})
				.finally(() => {
					this.isUpdatingStatus = false;
				});
		},

		getPreview() {
			if (!this.definition) {
				return;
			}

			const previewUuid = this.createPreviewUuid();
			this.previewError = false;
			this.isPDFLoading = true;

			new DocumentDefinitionRepository()
				.preview({
					uuid: previewUuid,
					key: this.definition.key,
					...this.getRequestData(),
				})
				.then(response => {
					if (!response || !response.data || !response.data.uuid || !response.data.url) {
						throw new Error('Invalid document preview response.');
					}
					this.uuid = response.data.uuid;
					this.pdfURL = response.data.url;
				})
				.catch(error => {
					this.previewError = true;
				})
				.finally(() => {
					this.isPDFLoading = false;
				});
		},

		createPreviewUuid() {
			if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
				return crypto.randomUUID();
			}

			return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
				const random = Math.floor(Math.random() * 16);

				const value = char === 'x' ? random : (random & 0x3) | 0x8;

				return value.toString(16);
			});
		},
	},
};
</script>

<style scoped>
.document-workspace {
	display: grid;
	grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
	gap: 32px;
	align-items: start;
	width: 100%;
	min-width: 0;
}

.document-editor-panel,
.document-preview-panel {
	min-width: 0;
	width: 100%;
}

.document-preview-empty {
	min-height: 400px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #6b7280;
}

.document-elements-panel {
	width: 100%;
	margin-top: 24px;
	border: 1px solid #e5e7eb;
	border-radius: 4px;
	background: #fff;
}

.document-elements-toggle {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 12px 16px;
	text-align: left;
	background: transparent;
	border: 0;
	cursor: pointer;
	outline: none;
}

.document-elements-toggle:focus,
.document-elements-toggle:active {
	outline: none;
	box-shadow: none;
}

.document-elements-content {
	padding: 16px;
	border-top: 1px solid #e5e7eb;
}

.document-definition-notice {
	padding: 10px 16px;
	border: 1px solid #3b82f6;
	border-radius: 0.25rem;
	background: #fff;
	color: #374151;
	font-size: 0.875rem;
	line-height: 1.25rem;
}

.preview-loading-spinner {
	transform: scale(0.6);
}

.document-header-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: nowrap;
	white-space: nowrap;
	margin-left: auto;
}

.document-header-toggle {
	display: flex;
	align-items: center;
	flex-shrink: 0;
	margin-left: 32px;
	gap: 16px;
}

.document-validation-notice {
	padding: 14px 16px;
	background: #f4f6f9;
	border: 1px solid #d5dce5;
	border-left: 3px solid #0068b4;
	border-radius: 4px;
	color: #333;
	font-size: 13px;
	text-align: left;
}

.document-validation-notice-title {
	display: flex;
	align-items: center;
	margin-bottom: 8px;
	color: #0068b4;
	font-weight: 700;
}

.document-validation-notice p {
	color: #434343;
	line-height: 1.5;
}

.document-validation-keys {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.document-validation-key {
	padding: 4px 8px;
	background: #fff;
	border: 1px solid #d5dce5;
	border-radius: 3px;
	color: #333;
	font-family: monospace;
	font-size: 12px;
}

.preview-loading-spinner {
	transform: scale(0.6);
	transform-origin: center;
}

.document-pdf-preview {
	position: relative;
	width: 100%;
	min-height: 740px;
}

.document-preview-overlay {
	position: absolute;
	inset: 0;
	z-index: 10;

	display: flex;
	align-items: center;
	justify-content: center;

	background: rgba(244, 246, 249, 0.65);
	backdrop-filter: blur(1px);

	border-radius: 4px;
}

.document-preview-overlay-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;

	padding: 20px 28px;

	background: #fff;
	border: 1px solid #d5dce5;
	border-radius: 4px;

	color: #333;
	font-size: 13px;
	text-align: center;

	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.document-preview-overlay-content .preview-loading-spinner {
	transform: scale(0.8);
	transform-origin: center;
}

@media (max-width: 1200px) {
	.document-workspace {
		grid-template-columns: 1fr;
	}

	.document-preview-panel {
		padding-left: 0;
		padding-top: 24px;
		border-left: none;
		border-top: 2px solid var(--connect-pro-blue, #1d4ed8);
	}
}
</style>
